/**
 * <FeaturesIndex> — public /features grid page.
 *
 * Shows all feature modules in one searchable grid, grouped by category.
 * Acts as the landing target when someone clicks the "Features" navbar item
 * directly (vs. hovering for the MegaMenu).
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import Navbar from './Navbar';
import Footer from './Footer';
import PageMeta from './PageMeta';
import { featurePages, featureGroups, featureList } from '../data/featuresContent';
import { Button } from './ui/button';
import { track } from '../services/analytics';

const ACCENT_TEXT = {
  green: 'text-green-700', blue: 'text-blue-700', orange: 'text-orange-700',
  purple: 'text-purple-700', amber: 'text-amber-700',
};
const ACCENT_BG = {
  green: 'bg-green-50', blue: 'bg-blue-50', orange: 'bg-orange-50',
  purple: 'bg-purple-50', amber: 'bg-amber-50',
};
const ACCENT_RING = {
  green: 'ring-green-200', blue: 'ring-blue-200', orange: 'ring-orange-200',
  purple: 'ring-purple-200', amber: 'ring-amber-200',
};

const FeaturesIndex = () => {
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://rentkollect.com';
  const pageUrl = `${origin}/features`;
  const ogImage = `${origin}/og-image.png`;

  // ItemList JSON-LD — gives Google + AI crawlers a clean catalog of
  // all 10 modules in one structured payload.
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'RentKollect — All product features',
    description: '10 production-grade modules for Indian PG, hostel, and co-living owners.',
    url: pageUrl,
    itemListElement: featureList.map((f, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      name: f.eyebrow,
      description: f.subtitle,
      url: `${origin}/features/${f.slug}`,
    })),
  };

  return (
    <div className="min-h-screen bg-white">
      <PageMeta
        title="Features — One platform for every PG module"
        description="Money, properties, tenants, leads, operations, WhatsApp, reports, reminders, listings, agreements — every module Indian PG owners need, in one app."
        path="/features"
        canonical={pageUrl}
        ogImage={ogImage}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <Navbar />

      {/* Hero */}
      <section className="pt-24 pb-14 bg-gradient-to-b from-blue-50 to-white relative">
        <div className="absolute inset-0 opacity-30 pointer-events-none"
             style={{ backgroundImage: 'radial-gradient(circle, #cbd5e1 1px, transparent 1px)', backgroundSize: '24px 24px' }} />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-blue-700 bg-white/80 backdrop-blur px-3 py-1 rounded-full ring-1 ring-blue-200">
            Every PG module, one platform
          </span>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-[1.1] tracking-tight">
            One app. Every module<br />Indian PG owners need.
          </h1>
          <p className="mt-5 text-lg text-gray-700 max-w-2xl mx-auto leading-relaxed">
            10 production-grade modules, all included. No add-on pricing, no per-module bills.
            Built and battle-tested with 500+ Indian PG owners.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              size="lg"
              onClick={() => { track('cta_features_index_trial'); window.location.href = '/manager/login'; }}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8"
              data-testid="features-index-trial-cta"
            >
              Start free trial
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => {
                track('cta_features_index_demo');
                document.getElementById('feature-grid')?.scrollIntoView({ behavior: 'smooth' });
              }}
              data-testid="features-index-explore-cta"
            >
              Explore features
            </Button>
          </div>
          <div className="mt-6 flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-gray-600">
            <span className="inline-flex items-center gap-1"><CheckCircle2 size={14} className="text-green-600" /> 30-day free trial</span>
            <span className="inline-flex items-center gap-1"><CheckCircle2 size={14} className="text-green-600" /> No credit card</span>
            <span className="inline-flex items-center gap-1"><CheckCircle2 size={14} className="text-green-600" /> Setup in 10 minutes</span>
          </div>
        </div>
      </section>

      {/* Grouped feature grid */}
      <section id="feature-grid" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-14">
          {featureGroups.map((group) => (
            <div key={group.label} data-testid={`features-group-${group.label.toLowerCase().replace(/[^a-z]/g, '-')}`}>
              <div className="flex items-end justify-between mb-6">
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400">{group.label}</p>
                  <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mt-1">{group.label}</h2>
                </div>
                <span className="text-sm text-gray-500">{group.items.length} modules</span>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {group.items.map((slug) => {
                  const f = featurePages[slug];
                  if (!f) return null;
                  const Icon = f.bullets[0]?.icon;
                  const textCls = ACCENT_TEXT[f.heroAccent] || 'text-gray-700';
                  const bgCls   = ACCENT_BG[f.heroAccent]   || 'bg-gray-50';
                  const ringCls = ACCENT_RING[f.heroAccent] || 'ring-gray-200';
                  return (
                    <Link
                      key={slug}
                      to={`/features/${slug}`}
                      onClick={() => track('cta_features_index_card_clicked', { slug })}
                      data-testid={`features-card-${slug}`}
                      className={`group block p-5 rounded-2xl bg-white ring-1 ${ringCls} hover:shadow-lg hover:-translate-y-0.5 transition-all`}
                    >
                      <div className={`w-11 h-11 rounded-lg ${bgCls} flex items-center justify-center mb-4`}>
                        {Icon && <Icon size={20} className={textCls} />}
                      </div>
                      <p className={`text-[11px] font-bold uppercase tracking-wider ${textCls}`}>{f.eyebrow}</p>
                      <h3 className="mt-1 font-bold text-gray-900 leading-snug">{f.title}</h3>
                      <p className="mt-2 text-sm text-gray-600 leading-relaxed line-clamp-3">{f.subtitle}</p>
                      <span className={`mt-4 inline-flex items-center text-sm font-semibold ${textCls} group-hover:gap-2 transition-all`}>
                        Learn more <ArrowRight size={14} className="ml-1" />
                      </span>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20 bg-blue-50">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
            Stop juggling 6 apps. Use 1.
          </h2>
          <p className="mt-3 text-gray-700 max-w-xl mx-auto">
            Set up your first PG in 10 minutes. Cancel anytime in the next 30 days, no questions.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              size="lg"
              onClick={() => { track('cta_features_index_bottom_trial'); window.location.href = '/manager/login'; }}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8"
              data-testid="features-index-bottom-trial"
            >
              Start free trial
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FeaturesIndex;
